import { ChangeEvent, FC, Fragment, useCallback, useContext, useEffect } from 'react'
import { useRouter } from 'next/router'
import { Button, Icon, Image, Select, Text, Textarea } from '@/client/components'
import { CartContext } from '@/client/context/cart'
import { useDebounce, useGetPrices, useMutatePrices } from '@/client/hooks'
import { SelectOptionProps } from '@/client/types'
import { useI18n } from '@/locales'
import * as S from './styles'
import {
  getCorrectDimensionType,
  getOptionsField,
  getOptionValue,
  getOptionValueForShortSummary,
  isDateReal,
  isExcludingField
} from '../helper'
import { ARTWORKSERVICE_SELECTION, Steps, SummaryChildProps } from '../types'

export const Summary: FC<SummaryChildProps> = ({
  calculatorFields,
  optionFields,
  setStep,
  texts,
  randomText,
  isArtworkService,
  production_time,
  template,
  compactView = false,
  serviceType
}) => {
  const { locale } = useRouter()
  const { updateSelection, selection } = useContext(CartContext)
  const { data } = useGetPrices(locale)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [order, no_artwork_service, ...restOptionFields] = optionFields // Small trap here: We need unused no_artwork_service as it would go otherwise in restOptionFields
  const { mutate } = useMutatePrices(locale)
  const t = useI18n()
  const debouncedSelection = useDebounce(selection, 500)

  useEffect(() => {
    mutate(debouncedSelection)
  }, [debouncedSelection])

  const keysToExclude = [
    'embossing_tool_material', // Previously used to indicate if a premium stamping tool is required.
    'paper',
    'quantity',
    'dimension',
    'diameter',
    'tpy_cta',
    'tpy_type',
    'tpy_layout',
    'number_of_sku',
    'tpy_lang',
    //hide this info for basic proof
    ...(serviceType === 'basic' ? ['label_type'] : [])
  ]

  const noArtworkServiceId =
    data?.optionFields
      .find((item) => item.key === 'artwork_service')
      ?.options.find((optionItem) => optionItem.key === 'no_artwork_service')?.id ||
    ARTWORKSERVICE_SELECTION.NO_ARTWORK_SERVICE

  const selectedOption = (key) =>
    getOptionsField(calculatorFields, key).find((option) => option.id === selection[key])?.name

  const selectDeliveryType = useCallback(
    (type) => {
      const newSelection = { ...selection, super_express_production: false, express_production: type }
      updateSelection(newSelection)
      mutate(newSelection)
    },
    [updateSelection, selection, mutate]
  )

  const selectSuperExpressDeliveryType = useCallback(
    (type) => {
      const newSelection = { ...selection, express_production: false, super_express_production: type }
      updateSelection(newSelection)
      mutate(newSelection)
    },
    [updateSelection, selection, mutate]
  )

  const handleChangeOrderName = useCallback(
    (optionId, value) => {
      updateSelection({
        ...selection,
        [optionId]: value
      })
    },
    [selection, updateSelection, mutate]
  )

  /**
   * BEGIN REL-776: Handling production options in a drop-down instead of button for each option, which is slimmer and waste
   * less space on the screen :-).
   */
  const PRODUCTION_TYPE_STANDARD = 'standard' as const // Not really used as a value
  const PRODUCTION_TYPE_EXPRESS = 'express_production' as const
  const PRODUCTION_TYPE_SUPER_EXPRESS = 'super_express_production' as const

  /**
   * Build the option list for the productions type. Production types that are not allowed will be filtered out or disabled.
   */
  const getProductionOptions = useCallback(() => {
    const expressIsHidden = template?.['express_production']?.hidden
    const superExpressIsHidden = template?.['super_express_production']?.hidden

    const options: SelectOptionProps[] = [
      {
        id: PRODUCTION_TYPE_STANDARD,
        name: `${t('PRODUCTION_TYPE_STANDARD')}${
          isDateReal(production_time.dateStandard)
            ? ` - ${t('PRODUCTION_TYPE_ESTIMATED_SHIPPING_DATE')} ${production_time.dateStandard}`
            : ''
        }`
      },
      ...(!expressIsHidden && isDateReal(production_time.dateExpress) // Only if date is available we show this production type
        ? [
            {
              id: PRODUCTION_TYPE_EXPRESS,
              name: `${t('PRODUCTION_TYPE_EXPRESS')} - ${t('PRODUCTION_TYPE_SHIPPING_DATE')} ${
                production_time.dateExpress
              }`
            }
          ]
        : []),
      ...(!superExpressIsHidden && isDateReal(production_time.dateSuperExpress) // Only if date is available we show this production type
        ? [
            {
              id: PRODUCTION_TYPE_SUPER_EXPRESS,
              name: `${t('PRODUCTION_TYPE_SUPER_EXPRESS')} - ${t('PRODUCTION_TYPE_SHIPPING_DATE')}  ${
                production_time.dateSuperExpress
              }`
            }
          ]
        : [])
    ]

    // We disable certain production types depending on artwork service, template restrictions and
    // whether the backend actually returned a valid production date for that type (e.g. some papers
    // do not support express production, in which case dateExpress/dateSuperExpress become invalid).
    return options.map((option) => ({
      id: option.id,
      name: option.name,
      disabled:
        (option.id == PRODUCTION_TYPE_EXPRESS &&
          (isArtworkService || !isDateReal(production_time.dateExpress) || template?.express_production?.disabled)) ||
        (option.id == PRODUCTION_TYPE_SUPER_EXPRESS &&
          (selection.proof ||
            selection.job_type ||
            isArtworkService ||
            selection.artwork_service > ARTWORKSERVICE_SELECTION.NO_ARTWORK_SERVICE ||
            !isDateReal(production_time.dateSuperExpress) ||
            template?.super_express_production?.disabled))
    }))
  }, [selection, isArtworkService, template, production_time])

  /** Get value for drop down */
  const getProductionTypeSelectValue = () => {
    // standard
    // Unfortunately, we have to use the == operator
    const isStandardProductionType =
      selection.super_express_production !== undefined
        ? selection?.express_production == false && selection?.super_express_production == false
        : true

    if (isStandardProductionType) {
      return PRODUCTION_TYPE_STANDARD
    }

    if (selection?.express_production == true && selection?.super_express_production == false) {
      return PRODUCTION_TYPE_EXPRESS
    }

    if (selection?.super_express_production == true) {
      return PRODUCTION_TYPE_SUPER_EXPRESS
    }

    return PRODUCTION_TYPE_STANDARD
  }

  /** Handle selection from the drop-down */
  const handleProductionTypeSelect = (selectedProductionType: string) => {
    switch (selectedProductionType) {
      case PRODUCTION_TYPE_STANDARD:
        selectDeliveryType(false)
        break
      case PRODUCTION_TYPE_EXPRESS:
        selectDeliveryType(true)
        break
      case PRODUCTION_TYPE_SUPER_EXPRESS:
        selectSuperExpressDeliveryType(true)
        break
      default:
        selectDeliveryType(false)
    }
  }

  // orderTemplates only: We do not allow changing a production type if all non-standard production types are disabled
  const isProductionTypeSelectDisabled = useCallback(
    () => template?.['express_production']?.disabled && template?.['super_express_production']?.disabled,
    [template]
  )

  // Hide the whole production type drop down in case all options are marked as hidden
  const isProductionTypeSelectHidden = useCallback(
    () => template?.['express_production']?.hidden && template?.['super_express_production']?.hidden,
    [template]
  )
  /**
   * END REL-776
   */

  useEffect(() => {
    if (!isDateReal(data?.price?.dateExpress || '')) {
      selectDeliveryType(false)
    }
  }, [data?.price?.dateExpress])
  return (
    <>
      <S.Summary>
        <S.SummaryHeader>
          {isArtworkService ? (
            <>
              <Text size='lg' family='heading' weight='bold' margin='0'>
                {randomText?.project_name}
              </Text>

              <Button size='small' kind='weak' isOutlined onClick={() => setStep(Steps.OPTIONS)}>
                <Icon name='edit' />
                {texts.edit_button}
              </Button>
            </>
          ) : (
            <>
              <Text size='lg' family='heading' weight='bold' margin='0'>
                {order.displayName}
              </Text>
            </>
          )}
        </S.SummaryHeader>
        <>
          {isArtworkService ? (
            <S.SummaryBody>
              <Text size='sm' color='weak' margin='0 0 0.125rem'>
                {randomText?.project_name}
              </Text>
              <Text weight='medium' margin='0'>
                {selection.artwork_service_project_name}
              </Text>
            </S.SummaryBody>
          ) : (
            <>
              {/* order name */}
              <Textarea
                key={order.options[0].id}
                id={order.options[0].id}
                isBlock
                name={order.options[0].key}
                label={order.options[0].name}
                value={selection[order.options[0].key] as string}
                dimension='small'
                onChange={(event: ChangeEvent<HTMLTextAreaElement>): void => {
                  handleChangeOrderName(order.options[0].key, event?.currentTarget?.value)
                }}
                style={{ borderRadius: '10px' }}
              />
            </>
          )}
        </>
      </S.Summary>

      {!compactView && (
        <S.Summary>
          <S.SummaryHeader>
            <Text size='lg' family='heading' weight='bold' margin='0'>
              {texts.tag}
            </Text>
            <Button size='small' kind='weak' isOutlined onClick={() => setStep(Steps.SIZE_AND_FORMAT)}>
              <Icon name='edit' />
              {texts.edit_button}
            </Button>
          </S.SummaryHeader>

          <>
            <S.MainSummary>
              <S.ShortSummary>
                <S.PositionSvg>
                  <Image src={`/icons/rolls-icon.svg`} width='319' height='490' alt='rolls' />
                  <S.PositionTextInSvg>
                    <Text size='sm' color='normal' margin='0 0 3rem 0' weight='bold'>
                      {randomText?.quick_summary}
                    </Text>
                    <>
                      <>
                        <>
                          <S.IconAligner>
                            <Icon name='dimension' size='s5' />
                            <Text size='sm' color='weak' margin='0 0 1rem 1rem'>
                              {
                                calculatorFields.find(
                                  (field) => field.key === getCorrectDimensionType(selection, calculatorFields)
                                )?.displayName
                              }
                            </Text>
                          </S.IconAligner>
                          <Text
                            size='xlg'
                            color='primary'
                            margin='0 0 1rem 0'
                            weight='bold'
                            style={{ borderBottom: '2px dashed #EAEDF0' }}
                          >
                            {getOptionValueForShortSummary(
                              selection,
                              getCorrectDimensionType(selection, calculatorFields)
                            )}
                          </Text>
                        </>

                        {isArtworkService ? (
                          <>
                            <S.IconAligner>
                              <Icon name='quantity-icon' size='s5' />
                              <Text size='sm' color='weak' margin='0 0 1rem 1rem'>
                                {
                                  calculatorFields
                                    ?.find((field) => field.key === 'quantity')
                                    ?.options.find((option) => option.key === 'sorts')?.name
                                }
                              </Text>
                            </S.IconAligner>
                            <Text
                              size='xlg'
                              color='primary'
                              margin='0 0 1rem 0'
                              weight='bold'
                              style={{ borderBottom: '2px dashed #EAEDF0' }}
                            >
                              {getOptionValueForShortSummary(selection, 'sorts')}
                            </Text>
                          </>
                        ) : (
                          <>
                            <S.IconAligner>
                              <Icon name='quantity-icon' size='s5' />
                              <Text size='sm' color='weak' margin='0 0 1rem 1rem'>
                                {calculatorFields.find((field) => field.key === 'quantity')?.displayName}
                              </Text>
                            </S.IconAligner>
                            <Text
                              size='xlg'
                              color='primary'
                              margin='0 0 1rem 0'
                              weight='bold'
                              style={{ borderBottom: '2px dashed #EAEDF0' }}
                            >
                              {getOptionValueForShortSummary(selection, 'quantity')}
                            </Text>
                          </>
                        )}
                        <>
                          <S.IconAligner>
                            <Icon name='material-icon' size='s5' />
                            <Text size='sm' color='weak' margin='0 0 1rem 1rem'>
                              {calculatorFields.find((field) => field.key === 'paper')?.displayName}
                            </Text>
                          </S.IconAligner>
                          <Text
                            size='xs'
                            color='primary'
                            margin='0 0 1rem 0'
                            weight='bold'
                            style={{ borderBottom: '2px dashed #EAEDF0' }}
                          >
                            {selectedOption('paper')}
                          </Text>
                        </>
                      </>
                    </>
                  </S.PositionTextInSvg>
                </S.PositionSvg>
              </S.ShortSummary>
              <S.SummaryBody>
                <S.DetailedSummary>
                  {calculatorFields.map(
                    ({ key, displayName, options, exclude }) =>
                      isExcludingField(exclude, selection) &&
                      !keysToExclude.includes(key) && (
                        <Fragment key={key}>
                          <div style={{ display: 'inline-block' }}>
                            <Text size='sm' color='weak' margin='0 0 0.125rem'>
                              {displayName}
                            </Text>
                            <Text weight='medium' margin='0 0 1.5rem'>
                              {getOptionValue(options, selection, key)}
                            </Text>
                          </div>
                          <br />
                        </Fragment>
                      )
                  )}
                  <>
                    <div>
                      <Text size='sm' color='weak' margin='0 0 0.125rem'>
                        {randomText?.notes}
                      </Text>
                      <Text weight='medium' margin='0 0 1.5rem'>
                        {selection.artwork_service_description ? selection.artwork_service_description : '-'}
                      </Text>
                    </div>
                    <br />
                  </>
                </S.DetailedSummary>
              </S.SummaryBody>
            </S.MainSummary>
          </>
        </S.Summary>
      )}

      <>
        {locale && !['au', 'us'].includes(locale) && (
          <S.Summary hidden={isProductionTypeSelectHidden()}>
            <S.SummaryHeader>
              <Text size='lg' family='heading' weight='bold' margin='0'>
                {texts.production_details.heading}
              </Text>
            </S.SummaryHeader>

            {compactView && (
              <S.Production>
                <Select
                  id={'production_type_select'} // Does not matter
                  name={'production_type_select'} // Does not matter
                  options={getProductionOptions()}
                  value={getProductionTypeSelectValue()}
                  onChange={(e) => {
                    handleProductionTypeSelect(e.target.value)
                  }}
                  disabled={isProductionTypeSelectDisabled()}
                />
              </S.Production>
            )}

            {!compactView && (
              <S.Production>
                {!selection.proof && !selection.job_type && !isArtworkService && (
                  <S.ProductionType
                    role='button'
                    tabIndex={0}
                    selected={selection?.super_express_production === true}
                    disabled={
                      !(
                        isDateReal(production_time.dateSuperExpress) && selection.artwork_service === noArtworkServiceId
                      ) || template?.super_express_production?.disabled
                    }
                    onClick={() =>
                      isDateReal(production_time.dateSuperExpress) && selection.artwork_service === noArtworkServiceId
                        ? selectSuperExpressDeliveryType(true)
                        : false
                    }
                  >
                    <Icon name='super-express-production' className='super-express-production-icon-main' size='s7' />
                    <S.ProductionTypeWrapper>
                      <S.ProductionTypeHeader>
                        <Text
                          size='md'
                          weight={selection?.super_express_production === true ? 'bold' : 'normal'}
                          margin='0'
                          lineHeight='1.2'
                        >
                          <span
                            className='production-type-text-block'
                            dangerouslySetInnerHTML={{ __html: texts.production_details.super_express_button }}
                          />
                        </Text>
                      </S.ProductionTypeHeader>

                      {isDateReal(production_time.dateSuperExpress) && (
                        <Text size='sm' margin='0' color='danger' weight='bold'>
                          {production_time.dateSuperExpress}
                        </Text>
                      )}
                    </S.ProductionTypeWrapper>

                    <Image src={'/img/super-express-production.png'} height='11' width='17' alt='express-arrow' />
                  </S.ProductionType>
                )}

                {!isArtworkService && ( //Hide express production for standalone artwork service
                  <S.ProductionType
                    role='button'
                    tabIndex={0}
                    disabled={!isDateReal(production_time.dateExpress) || template?.express_production?.disabled}
                    selected={selection?.express_production === true && selection?.super_express_production === false}
                    onClick={() => (isDateReal(production_time.dateExpress) ? selectDeliveryType(true) : false)}
                  >
                    <Icon name='express-production-48h' className='express-production-48h' size='s7' />
                    <S.ProductionTypeWrapper>
                      <S.ProductionTypeHeader>
                        <Text
                          size='md'
                          weight={selection?.express_production === true ? 'bold' : 'normal'}
                          margin='0'
                          lineHeight='1.2'
                        >
                          <span
                            className='production-type-text-block'
                            dangerouslySetInnerHTML={{ __html: texts.production_details.express_button }}
                          />
                        </Text>
                      </S.ProductionTypeHeader>

                      {isDateReal(production_time.dateExpress) && (
                        <Text size='sm' margin='0' color='warning-light' weight='bold'>
                          {production_time.dateExpress}
                        </Text>
                      )}
                    </S.ProductionTypeWrapper>

                    <Image src={'/img/express-production.png'} height='11' width='12' alt='express-arrow' />
                  </S.ProductionType>
                )}

                <S.ProductionType
                  role='button'
                  tabIndex={0}
                  disabled={
                    !isDateReal(production_time.dateStandard) ||
                    (template?.express_production?.disabled && template?.super_express_production?.disabled)
                  }
                  selected={
                    selection.super_express_production !== undefined
                      ? selection?.express_production === false && selection?.super_express_production === false
                      : true
                  }
                  onClick={() => (isDateReal(production_time.dateStandard) ? selectDeliveryType(false) : false)}
                >
                  <Icon name='proof-production-time' size='s7' className='standard-icon' />
                  <S.ProductionTypeWrapper>
                    <S.ProductionTypeHeader>
                      <Text
                        size='md'
                        weight={selection?.express_production === false ? 'bold' : 'normal'}
                        margin='0'
                        lineHeight='1.2'
                      >
                        <span
                          className='production-type-text-block'
                          dangerouslySetInnerHTML={{ __html: texts.production_details.standard_button }}
                        />
                      </Text>
                    </S.ProductionTypeHeader>

                    {isDateReal(production_time.dateStandard) && (
                      <Text size='sm' margin='0' color='primary' weight='bold'>
                        {production_time.dateStandard}
                      </Text>
                    )}
                  </S.ProductionTypeWrapper>
                  <Image src={'/img/standard-production.png'} height='11' width='7' alt='standard-arrow' />
                </S.ProductionType>
              </S.Production>
            )}
            {!compactView && (
              <Text size='xs' margin='0' lineHeight='1.4'>
                {texts.production_details.description}
              </Text>
            )}
          </S.Summary>
        )}
      </>

      <>
        {!compactView && !isArtworkService && (
          <S.Summary>
            <S.SummaryHeader>
              <Text size='lg' family='heading' weight='bold' margin='0'>
                {texts.options}
              </Text>
              <Button size='small' kind='weak' isOutlined onClick={() => setStep(Steps.OPTIONS)}>
                <Icon name='edit' />
                {texts.edit_button}
              </Button>
            </S.SummaryHeader>

            <S.SummaryBody>
              {restOptionFields.map(({ key, displayName, options }) => (
                <Fragment key={key}>
                  <Text size='sm' color='weak' margin='0 0 0.125rem'>
                    {displayName}
                  </Text>
                  <Text weight='medium' margin='0 0 1.5rem'>
                    {getOptionValue(options, selection, key)}
                  </Text>
                </Fragment>
              ))}
            </S.SummaryBody>
          </S.Summary>
        )}
      </>
    </>
  )
}
